import { Add, Delete, Edit, RemoveRedEye } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Button,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import React, { useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import AddCompanyJob from "./partials/AddCompanyJobs/AddCompanyJobs";
import DeleteCompanyJob from "./partials/DeleteCompanyJobs/DeleteCompanyJobs";
import {
  ICompanyJob,
  ICompanyJobInput,
  ICompanyJobInputEdit,
} from "models/companyJob";
import {
  CompanyJobsCells,
  addCompanyJob,
  deleteCompanyJob,
  editCompanyJob,
  getCompanyJobs,
} from "framework/companyjobs";
import { useTranslation } from "react-i18next";
import { getJobs } from "framework/jobs";
import { IJob } from "models/job";
import { getPermission, getUserData } from "helpers";
import { Link } from "react-router-dom";
import BlockIcon from "@mui/icons-material/Block";
import { ICompanyDepartments } from "models/companyDepartment";
import { getCompanyDepartments } from "framework/companyDepartments";
import EditCompanyJob from "./partials/EditCompanyJobs/EditCompanyJobs";
import useFetchSection from "hooks/useFetchSection";
import { UserRoleName } from "types/auth";
import { importCompetency, importJobs } from "framework/competencies";
interface Props {
  feature: Feature;
}

const CompanyJobs: React.FC<Props> = ({ feature }) => {
  const [loading2, setLoading2] = useState<boolean>(false);

  const [FilterCompanyJobs, setFilterCompanyJobs] = useState<ICompanyJob[]>(
    [] as ICompanyJob[]
  );
  const { t, i18n } = useTranslation();
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedCompanyJob, setSelectedCompanyJob] = useState<ICompanyJob>(
    {} as ICompanyJob
  );

  const [inputObj, setInputObj] = useState<ICompanyJobInputEdit>(
    {} as ICompanyJobInputEdit
  );

  const [Departments, setDepartments] = useState<ICompanyDepartments[]>(
    [] as ICompanyDepartments[]
  );
  const [Jobs, setJobs] = useState<IJob[]>([] as IJob[]);
  const { setAlert } = useAlert();
  const user = getUserData();
  const { colors, roleName } = user;
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addDepartmentsMutation = useMutation({
    mutationFn: (createInput: ICompanyJobInput) => {
      return addCompanyJob(createInput);
    },
  });

  const editDepartmentsMutation = useMutation({
    mutationFn: (createInput: ICompanyJobInputEdit) => {
      return editCompanyJob(createInput, selectedCompanyJob.id);
    },
  });

  const deleteDepartmentsMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteCompanyJob(id);
    },
  });

  const importJobsMutation = useMutation({
    mutationFn: () => {
      return importJobs();
    },
  });

  const fetchDepartments = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery(
        "get Company Departments",
        () => getCompanyDepartments()
      );

      setDepartments(response);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  const fetchJobs = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get Jobs", () =>
        getJobs()
      );

      setJobs(response);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchDepartments();
    fetchJobs();
    // eslint-disable-next-line
  }, []);

  const {
    loading,
    data: CompanyJobs,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getCompanyJobs(),
    fetchPermission: () => getPermission(16),
    section: "Company Jobs",
    onSuccess: (data) => setFilterCompanyJobs(data),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleOpenAdd = () => setOpenAdd(true);

  const handleCloseAdd = () => {
    setOpenAdd(false);
    setSelectedCompanyJob({} as ICompanyJob);
  };

  const handleOpenEdit = (Country: ICompanyJob) => {
    setSelectedCompanyJob(Country);
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedCompanyJob({} as ICompanyJob);
  };

  const handleOpenDelete = (Country: ICompanyJob) => {
    setSelectedCompanyJob(Country);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedCompanyJob({} as ICompanyJob);
  };

  const getBodyCells = (row: ICompanyJob) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "companyDepartmentId")
        return {
          id: "companyDepartmentId",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{Departments.find((x: ICompanyDepartments) => x.id === row?.companyDepartmentId)?.[(i18n.language === 'en' ? 'name' : 'nameAr')]}</Typography>
            </TableCell>
          ),
        };

      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };

      // if (key === "companyDepartmentId")
      //   return {
      //     id: "companyDepartmentId",
      //     element: (
      //       <TableCell align={"left"} key={i}>
      //         <Typography variant="body2">
      //           {row?.companyDepartmentId}
      //         </Typography>
      //       </TableCell>
      //     ),
      //   };

      // if (key === "departmentNameAr")
      //   return {
      //     id: "departmentNameAr",
      //     element: (
      //       <TableCell align={"left"} key={i}>
      //         <Typography variant="body2">{row?.departmentNameAr}</Typography>
      //       </TableCell>
      //     ),
      //   };

      // if (key === "customTitle")
      //   return {
      //     id: "customTitle",
      //     element: (
      //       <TableCell align={"left"} key={i}>
      //         <Typography variant="body2">{row?.customTitle}</Typography>
      //       </TableCell>
      //     ),
      //   };

      // if (key === "customTitleAr")
      //   return {
      //     id: "customTitleAr",
      //     element: (
      //       <TableCell align={"left"} key={i}>
      //         <Typography variant="body2">{row?.customTitleAr}</Typography>
      //       </TableCell>
      //     ),
      //   };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title={t("T_details")}>
                  <Link to={`/companyJobCompetencies/${row.id}`}>
                    <IconButton size={"small"} color={"info"}>
                      <RemoveRedEye
                        sx={{
                          color: colors.viewIconColor
                            ? colors.viewIconColor
                            : "#0288d1",
                          "&:hover": { color: colors?.viewIconHoverColor },
                        }}
                      />
                    </IconButton>
                  </Link>
                </Tooltip>
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async (data: any[], department: string) => {
    if (!!!data.length || !department)
      return setAlert({
        open: true,
        message: "Select required fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addDepartmentsMutation.mutateAsync({
        companyDepartmentId: department,
        jobs: data,
      });
      setAlert({
        open: true,
        message: "Company Job added successfully",
        type: "success",
      });
      revalidate();
      fetchDepartments();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);

      await editDepartmentsMutation.mutateAsync({
        id: selectedCompanyJob.id,
        companyDepartmentId:
          inputObj.companyDepartmentId ||
          selectedCompanyJob.companyDepartmentId,
        name: inputObj.name || selectedCompanyJob.name,
        nameAr: inputObj.nameAr || selectedCompanyJob.nameAr,
      });
      setAlert({
        open: true,
        message: "Company Job Edited successfully",
        type: "success",
      });
      revalidate();
      fetchDepartments();
      handleCloseEdit();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteDepartmentsMutation.mutateAsync(selectedCompanyJob.id);
      setAlert({
        open: true,
        message: "Company Job deleted successfully",
        type: "success",
      });
      revalidate();
      fetchDepartments();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterCompanyJobs(CompanyJobs!);
    let filtered = CompanyJobs!.filter((companyJob) =>
      companyJob.name.toLowerCase().includes(value.toLowerCase())
    );
    setFilterCompanyJobs(filtered);
  };

  // const handleSearchDepartment = async (value: string) => {
  //   if (!value) return setFilterCompanyJobs(CompanyJobs!);
  //   let filtered = CompanyJobs!.filter((companyJob) =>
  //     companyJob.departmentName.toLowerCase().includes(value.toLowerCase())
  //   );
  //   setFilterCompanyJobs(filtered);
  // }; {roleName === UserRoleName.COMPANY_ADMIN && <div style={{ display: "flex", justifyContent: "flex-end" }}>


  const handleImport = async () => {
    try {
      setLoading2(true);

      await importJobsMutation.mutateAsync();
      setAlert({
        open: true,
        message: "Jobs successfully imported from the master list ",
        type: "success",
      });
      revalidate();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {/* <Search
              handleChange={handleSearchDepartment}
              placeholder={t("T_department")}
            /> */}
            {permission?.hasModifyPermission && (
              <Tooltip title="Add CompanyJobs">
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {/* {roleName === UserRoleName.COMPANY_ADMIN && <div style={{ display: "flex", justifyContent: "flex-end" }}>
  <Button
    variant="contained" onClick={handleImport} >
     {t('T_import_from_master')}
    </Button>
</div>} */}

        {permission?.hasReadPermission! && FilterCompanyJobs.length > 0 && (
          <List
            headCells={CompanyJobsCells}
            data={FilterCompanyJobs}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterCompanyJobs || FilterCompanyJobs.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_job_header")}
              message={t("T_no_job_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddCompanyJob
          open={openAdd}
          loading={loading2}
          Departments={Departments}
          companyJobs={CompanyJobs!}
          Jobs={Jobs}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
        />
      )}

      {openEdit && (
        <EditCompanyJob
          onChanges={handleChange}
          open={openEdit}
          loading={loading2}
          Departments={Departments}
          companyJob={selectedCompanyJob}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
        />
      )}
      {openDelete && (
        <DeleteCompanyJob
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default CompanyJobs;
